import {
  Cloud,
  Edition,
  AwsMpAccount,
  SignupUrlParameter,
  MarketingUrlParameter,
  mapToLegacySnowflakeRegionName,
  isCocoSubscriptionPromoProgram,
} from '@signup/shared';
import queryString from 'query-string';
import * as _ from 'lodash';
import * as configs from './configurations';

function getLastParam(param: string | string[]) {
  if (Array.isArray(param)) {
    return param[param.length - 1];
  } else {
    return param;
  }
}
export interface MarketingParams {
  [MarketingUrlParameter.UTM_ADGROUP]?: string;
  [MarketingUrlParameter.UTM_SOURCE]?: string;
  [MarketingUrlParameter.UTM_MEDIUM]?: string;
  [MarketingUrlParameter.UTM_CAMPAIGN]?: string;
  [MarketingUrlParameter.UTM_CONTENT]?: string;
  [MarketingUrlParameter.UTM_TERM]?: string;
  [MarketingUrlParameter.UTM_REGION]?: string;
  [MarketingUrlParameter.GCLID]?: string;
}

export interface SignupParams {
  plan?: Edition;
  planp?: Edition;
  cloud?: Cloud;
  cloudp?: Cloud;
  region?: string;
  regionp?: string;
  owner?: string;
  eng?: string; // Sales Engineer Email
  ampt?: string; // Amazon Marketplace Token
  sdm?: boolean; // Snowflake Data Marketplace
  listing?: string; //global name of marketplace listing
  referrer?: string; //where did the signup come from (ex. snowsight)
  abexperiment?: string; // for guranteeing inclusion in an experiment
  account?: string; // Encrypted SFDC account ID the signup is associated with
  awsmp?: AwsMpAccount; // AWS Marketplace account the request originated from
  lab?: string; //id of the lab where the user initiated the trial experience
  template?: string; //id of template the user will land at
  workspaces?: string; //workspaces persona type
  firstname?: string;
  lastname?: string;
  email?: string;
  E6CCEE4CEB?: string; //SNOW-2696096
  token?: string;
  k?: string;
  t?: string;
  program?: string;
}

export const parseQueryParams = (
  paramString: string | undefined,
): [SignupParams, MarketingParams] => {
  const filteredSignupParams: SignupParams = {};
  let marketingParams: MarketingParams = {};

  if (paramString) {
    const parsedParams = queryString.parse(paramString);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const rawParams: any = _.mapValues(parsedParams, (values: string | string[]) =>
      getLastParam(values),
    );

    marketingParams = _.pick(rawParams, [
      MarketingUrlParameter.UTM_ADGROUP,
      MarketingUrlParameter.UTM_SOURCE,
      MarketingUrlParameter.UTM_MEDIUM,
      MarketingUrlParameter.UTM_CAMPAIGN,
      MarketingUrlParameter.UTM_CONTENT,
      MarketingUrlParameter.UTM_TERM,
      MarketingUrlParameter.UTM_REGION,
      MarketingUrlParameter.GCLID,
    ]);

    //NOTE: the param values differ from the Snowflake edition names
    const paramToEditionMapping: { [key: string]: Edition } = {
      standard: Edition.STANDARD,
      enterprise: Edition.ENTERPRISE,
      business_critical: Edition.BUSINESS_CRITICAL,
      enterprise_sensitive: Edition.BUSINESS_CRITICAL,
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handlePlanParam = (value: any) => {
      if (value) {
        const edition = paramToEditionMapping[value.toLowerCase()];
        if (edition) {
          filteredSignupParams[SignupUrlParameter.PLAN] = edition;
        }
      }
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handlePlanPreferenceParam = (value: any) => {
      if (value) {
        const edition = paramToEditionMapping[value.toLowerCase()];
        if (edition) {
          filteredSignupParams[SignupUrlParameter.PLAN_PREFERENCE] = edition;
        }
      }
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleCloudParam = (value: any) => {
      if (Object.values(Cloud).some(e => e === value)) {
        filteredSignupParams[SignupUrlParameter.CLOUD] = value as Cloud;
      }
      if (value?.toLowerCase() === 'sdm') {
        filteredSignupParams['sdm'] = true;
      }
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleCloudPreferenceParam = (value: any) => {
      if (Object.values(Cloud).some(e => e === value)) {
        filteredSignupParams[SignupUrlParameter.CLOUD_PREFERENCE] = value as Cloud;
      }
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleEngParam = (value: any) => {
      filteredSignupParams[SignupUrlParameter.ENG] = value.trim();
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleOwnerParam = (value: any) => {
      // Salesforce field length restriction means this can only ever be 64 characters long
      filteredSignupParams[SignupUrlParameter.OWNER] = value.trim().substring(0, 64);
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleAmptParam = (value: any) => {
      filteredSignupParams[SignupUrlParameter.AMPT] = decodeURIComponent(value);
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleSdmParam = (value: any) => {
      filteredSignupParams['sdm'] = true;
      filteredSignupParams[SignupUrlParameter.OWNER] = `${value}-referral`;
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleListingParam = (value: any) => {
      filteredSignupParams[SignupUrlParameter.LISTING] = value;
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleRegionParam = (value: any) => {
      const deployments = configs.getDeployments();
      const region = mapToLegacySnowflakeRegionName(value);
      const cloud = deployments[region]?.cloud;
      if (cloud != undefined) {
        // always lock in the cloud based on region, and either default or lock in region
        filteredSignupParams[SignupUrlParameter.CLOUD] = cloud;
        filteredSignupParams[SignupUrlParameter.REGION] = region;
      }
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleRegionPreferenceParam = (value: any) => {
      const deployments = configs.getDeployments();
      const region = mapToLegacySnowflakeRegionName(value);
      const cloud = deployments[region]?.cloud;
      if (cloud != undefined) {
        // always lock in the cloud based on region, and either default or lock in region
        filteredSignupParams[SignupUrlParameter.CLOUD] = cloud;
        filteredSignupParams[SignupUrlParameter.REGION_PREFERENCE] = region;
      }
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleReferrer = (value: any) => {
      filteredSignupParams[SignupUrlParameter.REFERRER] = value;
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleAbExperiment = (value: any) => {
      filteredSignupParams[SignupUrlParameter.ABEXPERIMENT] = value;
    };

    const handleAccountParam = (value: string) => {
      filteredSignupParams[SignupUrlParameter.ACCOUNT] = value;
    };

    const handleAwsMpParam = (value: string) => {
      if (Object.values(AwsMpAccount).some(str => str === value)) {
        filteredSignupParams[SignupUrlParameter.AWSMP] = value as AwsMpAccount;
      }
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleLabParam = (value: any) => {
      filteredSignupParams[SignupUrlParameter.LAB] = value;
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleTemplateIdParam = (value: any) => {
      filteredSignupParams[SignupUrlParameter.TEMPLATE] = value;
    };

    const handleWorkspacesParam = (value: unknown) => {
      if (typeof value === 'string') {
        filteredSignupParams[SignupUrlParameter.WORKSPACES] = value;
      }
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleFirstNameParam = (value: any) => {
      filteredSignupParams[SignupUrlParameter.FIRSTNAME] = value;
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleLastNameParam = (value: any) => {
      filteredSignupParams[SignupUrlParameter.LASTNAME] = value;
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleEmailParam = (value: any) => {
      filteredSignupParams[SignupUrlParameter.EMAIL] = value;
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleCustomParam = (value: any) => {
      //SNOW-2696096
      if (value) {
        filteredSignupParams[SignupUrlParameter.E6CCEE4CEB] = value;
      }
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleTokenParam = (value: any) => {
      if (value) {
        filteredSignupParams[SignupUrlParameter.TOKEN] = value;
      }
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleCcTokenParam = (value: any) => {
      if (value) {
        filteredSignupParams[SignupUrlParameter.T] = value;
      }
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleProgramParam = (value: any) => {
      // program gates the promo page, so a repeated param is treated as invalid rather than taking
      // the last value. The server rejects it the same way (see determineSignupFlowType).
      if (
        !Array.isArray(parsedParams[SignupUrlParameter.PROGRAM]) &&
        isCocoSubscriptionPromoProgram(value)
      ) {
        filteredSignupParams[SignupUrlParameter.PROGRAM] = value;
      }
    };

    //IMPORTANT: Insertion order into the map matters! (ex. check region after
    //cloud, and regionp before region)
    const signupParamToHandlerMapping = new Map([
      [SignupUrlParameter.PLAN_PREFERENCE, handlePlanPreferenceParam],
      [SignupUrlParameter.PLAN, handlePlanParam],
      [SignupUrlParameter.CLOUD_PREFERENCE, handleCloudPreferenceParam],
      [SignupUrlParameter.CLOUD, handleCloudParam],
      [SignupUrlParameter.REGION_PREFERENCE, handleRegionPreferenceParam],
      [SignupUrlParameter.REGION, handleRegionParam],
      [SignupUrlParameter.OWNER, handleOwnerParam],
      [SignupUrlParameter.ENG, handleEngParam],
      [SignupUrlParameter.AMPT, handleAmptParam],
      [SignupUrlParameter.SDM, handleSdmParam],
      [SignupUrlParameter.LISTING, handleListingParam],
      [SignupUrlParameter.REFERRER, handleReferrer],
      [SignupUrlParameter.ABEXPERIMENT, handleAbExperiment],
      [SignupUrlParameter.ACCOUNT, handleAccountParam],
      [SignupUrlParameter.AWSMP, handleAwsMpParam],
      [SignupUrlParameter.LAB, handleLabParam],
      [SignupUrlParameter.TEMPLATE, handleTemplateIdParam],
      [SignupUrlParameter.WORKSPACES, handleWorkspacesParam],
      [SignupUrlParameter.FIRSTNAME, handleFirstNameParam],
      [SignupUrlParameter.LASTNAME, handleLastNameParam],
      [SignupUrlParameter.EMAIL, handleEmailParam],
      [SignupUrlParameter.E6CCEE4CEB, handleCustomParam],
      [SignupUrlParameter.TOKEN, handleTokenParam],
      [SignupUrlParameter.T, handleCcTokenParam],
      [SignupUrlParameter.PROGRAM, handleProgramParam],
    ]);

    signupParamToHandlerMapping.forEach((handler, param) => {
      const value = rawParams[param];
      if (value) {
        handler(value);
      }
    });
  }
  return [filteredSignupParams, marketingParams];
};
